/**
 * Per-device learning progress for anonymous course readers (no login exists).
 * Namespaced by module slug so progress never crosses lessons. Safe to call
 * even when storage is unavailable (private browsing, disabled storage).
 */
const PREFIX = "course-progress:";

const readRecord = (key: string): Record<string, boolean> => {
  try {
    const raw = window.localStorage.getItem(PREFIX + key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeRecord = (key: string, value: Record<string, boolean>): void => {
  try {
    window.localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Storage unavailable; progress simply won't persist this session.
  }
};

export const getItemStatus = (
  scope: "flashcards" | "quiz",
  moduleSlug: string,
  itemId: string
): boolean => {
  const record = readRecord(`${scope}:${moduleSlug}`);
  return !!record[itemId];
};

export const setItemStatus = (
  scope: "flashcards" | "quiz",
  moduleSlug: string,
  itemId: string,
  known: boolean
): void => {
  const key = `${scope}:${moduleSlug}`;
  const record = readRecord(key);
  record[itemId] = known;
  writeRecord(key, record);
};

export const getAllStatuses = (
  scope: "flashcards" | "quiz",
  moduleSlug: string
): Record<string, boolean> => readRecord(`${scope}:${moduleSlug}`);

export const resetStatuses = (scope: "flashcards" | "quiz", moduleSlug: string): void => {
  writeRecord(`${scope}:${moduleSlug}`, {});
};

export const getPreferredFormat = (moduleSlug: string): string | null => {
  try {
    return window.localStorage.getItem(`${PREFIX}format:${moduleSlug}`);
  } catch {
    return null;
  }
};

export const setPreferredFormat = (moduleSlug: string, format: string): void => {
  try {
    window.localStorage.setItem(`${PREFIX}format:${moduleSlug}`, format);
  } catch {
    // ignore
  }
};

/** Last module a reader opened in a course, so "Continue" can return them there. */
export const getLastModule = (courseSlug: string): string | null => {
  try {
    return window.localStorage.getItem(`${PREFIX}last-module:${courseSlug}`);
  } catch {
    return null;
  }
};

export const setLastModule = (courseSlug: string, moduleSlug: string): void => {
  try {
    window.localStorage.setItem(`${PREFIX}last-module:${courseSlug}`, moduleSlug);
  } catch {
    // ignore
  }
};
